import React, { useContext } from "react";
import "./ResourceTable.css";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  Editicon,
  Deleteicon,
  Viewicon,
} from "../../../../../Components/IconComponents";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import Table from "../../../../../Components/UiComponents/Table/Table";
import { Modal, Button } from "react-bootstrap";
import Moment from "react-moment";
import { resourcemgmtcolumnsForExport } from "../../../../../JsonData/ResourceManagementConfig";
import { getData, putDataWithBody } from "../../../../../Services/Services";
import { setTableData } from "../../../../../CentralStore/ExportLMSTable/dataSlice";
import { getExportedColumnsData } from "../../../../../Components/UiComponents/utils";
import {
  resourceListURL,
  deleteResourcebyId,
  resourceCatListURL,
  resourceTypListURL,
  resourceActivityListURL,
  findRegionsByUser,
  getThemeURL,
  getMasterResourceListURL,
  resourceCatMasterListData,
  agencyListURL,
  getFacilities,
  getRestrictedRegionListURL,
} from "../../../../../Services/EndPoints";
import { Filter as FilterIcon } from "lucide-react";
import Confirmpopup from "../../../Common/ConfirmPopup/Confirmpopup";
import { useDispatch } from "react-redux";
import TextEllipsis from "../../../../../Components/UiComponents/TextEllipsis/TextEllipsis";
import Loader from "react-spinner-loader";
import { RoleContext } from "../../../../..";

// Combines isLowStock + lowStockThresholdPct into the single "Stock Status"
// export column (matches the in-table badge + threshold caption).
const formatStockStatus = (row) => {
  const status = row.isLowStock ? "Low Stock" : "In Stock";
  const thresholdPct = row.lowStockThresholdPct;
  return thresholdPct !== undefined && thresholdPct !== null
    ? `${status} (Threshold: ${thresholdPct}%)`
    : status;
};

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const filterControlStyle = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #E4E7EC",
  fontSize: "12px",
  background: "#fff",
  color: "#1D2939",
  height: "34px",
  boxSizing: "border-box",
  width: "100%",
};

const norm = (v) => String(v || "").trim().toLowerCase();
// Configured values first, then anything only present in the loaded rows; de-duplicated case-insensitively.
const buildOptions = (configured, fromData) => {
  const seen = new Set();
  const out = [];
  [...configured, ...fromData].forEach((raw) => {
    const label = String(raw || "").trim();
    if (label && !seen.has(norm(label))) {
      seen.add(norm(label));
      out.push(label);
    }
  });
  return out.sort((a, b) => a.localeCompare(b));
};

const STOCK_STATUS_OPTIONS = ["In Stock", "Low Stock"];

const ResourceTable = forwardRef((props, ref) => {
  const [loader, setLoader] = useState(false);
  const qs = require("qs");
  const [resourceData, setResourceData] = useState([]);
  const [resourceSrchdData, setResourceSrchdData] = useState(null);
  const [resourceCategoryData, setResourceCategoryData] = useState([]);
  const [resourceTypeData, setResourceTypeData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { roleName } = useContext(RoleContext);
 
  const [searchData, setSearchData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [configured, setConfigured] = useState({ names: [], categories: [], agencies: [], facilities: [], blocks: [] });
 
  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);


  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const userProfile = await getData(getThemeURL);
        const state_id = userProfile[0]?.state_id;
        const [districts, masters, categories, agencies, facilities, regions] = await Promise.all([
          getData(findRegionsByUser),
          getData(`${getMasterResourceListURL}/${state_id}`),
          getData(`${resourceCatMasterListData}/${state_id}`),
          getData(`${agencyListURL}/${state_id}`),
          getData(getFacilities.replace("STATEID", state_id)),
          getData(getRestrictedRegionListURL.replace("STATEID", state_id)),
        ]);
        const list = (x) => (Array.isArray(x) ? x : []);
        setDistrictData(list(districts).map((d) => d.value));
        setConfigured({
          names: list(masters).map((x) => x.name),
          categories: list(categories).map((x) => x.category),
          agencies: list(agencies).map((x) => x.name),
          facilities: list(facilities).map((x) => x.name),
          blocks: list(regions).map((x) => x.label),
        });
      } catch (error) {
        console.error("Error fetching resource filter options:", error);
      }
    };
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    getResourceList();
    getRsourceTypeList();
    if (props.locationInstance && props.locationInstance.msg != "") {
      toast.success(props.locationInstance.msg, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000, // Close after 2 seconds
      });
    }
  }, []);
 
  const addNewHandler = () => {
    navigate("/resource-list/create-resource");
  };
 
  // Helper function to sort data by creation date (newest first)
  const sortDataByCreatedDate = (data) => {
    return [...data].sort((a, b) => {
      // Assuming your data has a created_on or similar field
      // Adjust the field name based on your actual data structure
      const dateA = new Date(a.created_on || a.createdAt || a.created_date || 0);
      const dateB = new Date(b.created_on || b.createdAt || b.created_date || 0);
      return dateB - dateA; // Newest first
    });
  };
 
  const getResourceList = async () => {
    setLoader(true);
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var resourceList = await getData(`${resourceListURL}/${state_id}`);
    setLoader(false);
   
    // Sort the data with newest first
    const sortedResourceList = sortDataByCreatedDate(resourceList);
   
    setResourceData(sortedResourceList);
    // sendDataToCentralStore(sortedResourceList);
  };
 
  const searchByRsrcCategory = (event) => {
    var select_id = document.getElementById("resrcCatDropdown");
    let searchString =
      select_id.options[select_id.selectedIndex].text.toLowerCase();
    if (searchString != "all") {
      let rsrcCatArr = [];
      for (let resource in resourceData) {
        let actualString = resourceData[resource].category.toLowerCase();
        if (actualString.indexOf(searchString) > -1) {
          rsrcCatArr.push(resourceData[resource]);
        }
      }
      // Sort filtered data as well
      setResourceSrchdData(sortDataByCreatedDate(rsrcCatArr));
    } else {
      setResourceSrchdData(resourceData);
    }
  };
 
  const searchRsrcByType = (event) => {
    getRsourceCategoryList(event.target.value);
  };
 
  const getRsourceCategoryList = async (activity) => {
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var resourceCategoryList = await getData(resourceCatListURL + activity + "?stateId=" + state_id);
    for (let resourceCat in resourceCategoryList) {
      resourceCategoryList[resourceCat]["label"] =
        resourceCategoryList[resourceCat]["category"];
      resourceCategoryList[resourceCat]["value"] =
        resourceCategoryList[resourceCat]["category"];
    }
    setResourceCategoryData(resourceCategoryList);
  };
 
  const getRsourceTypeList = async () => {
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var resourceActList = await getData(`${resourceActivityListURL}/${state_id}`);
    for (let resource in resourceActList) {
      resourceActList[resource]["label"] =
        resourceActList[resource]["activity"];
      resourceActList[resource]["value"] =
        resourceActList[resource]["activity"];
    }
    //resourceActList.unshift({ id: "All", value: "ALL" });
    setResourceTypeData(resourceActList);
    getRsourceCategoryList(resourceActList[0]["activity"]);
  };
 
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = resourcemgmtcolumnsForExport;
      
      // Transform the data to include calculated available quantity
      const transformedData = data.map(row => ({
        ...row,
        available_quantity: (() => {
          const totalQuantity = row.total_quantity || 0;
          const damagedQuantity = row.damaged_quantity || 0;
          const deployQuantity = row.deploy_quantity || 0;

          const availableQuantity = totalQuantity - damagedQuantity - deployQuantity;
          return availableQuantity >= 0 ? availableQuantity : 0;
        })(),
        stockStatus: formatStockStatus(row),
      }));
      
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, transformedData),
        })
      );
    }
  };
 
  const columns = React.useMemo(
    () => [
      {
        Header: t("resourcetableheaders.resourcename"),
        accessor: "resource_name",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      // {
      //   Header: t("resourcetableheaders.resourceactivity"),
      //   accessor: "rsrc_activity_id",
      //   disableSortBy: true,
      // },
      {
        Header: t("resourcetableheaders.resourcecategory"),
        accessor: "rsrc_cat_id",
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
          Header: (
    <div style={{ textAlign: 'center' }}>
      Total Quantity<br/>(Incl. OOS/Damaged Quantity)
    </div>
  ),
  accessor: "total_quantity",
  disableSortBy: true,
        Cell: (props) => {
          return (
            <div style={{ textAlign: 'center', fontWeight: "bold" }}>
              {props.value ? props.value : "NA"}
            </div>
          );
        },
      },
      {
        Header: t("resourcetableheaders.damagedquantity"),
        accessor: "damaged_quantity",
        disableSortBy: true,
        Cell: (props) => {
          return (
            <div style={{ textAlign: 'center' }}>
              {props.value !== undefined && props.value !== null ? props.value : "NA"}
            </div>
          );
        },
      },
      {
        Header: t("resourcetableheaders.deployquantity"),
        accessor: "deploy_quantity",
        disableSortBy: true,
        Cell: (props) => {
          return (
            <div style={{ textAlign: 'center' }}>
              {props.value !== undefined && props.value !== null ? props.value : "0"}
            </div>
          );
        },
      },
      {
        Header: t("resourcetableheaders.availablequantity"),
        accessor: "available_quantity",
        disableSortBy: true,
        Cell: (props) => {
          // Calculate available quantity as total - damaged - deployed
          const totalQuantity = props.row.original.total_quantity || 0;
          const damagedQuantity = props.row.original.damaged_quantity || 0;
          const deployQuantity = props.row.original.deploy_quantity || 0;
          
          const availableQuantity = totalQuantity - damagedQuantity - deployQuantity;
          
          return (
            <div style={{ textAlign: 'center' }}>
              {availableQuantity >= 0 ? availableQuantity : 0}
            </div>
          );
        },
      },
      {
        Header: <div style={{ textAlign: "center" }}>Stock Status</div>,
        accessor: "isLowStock",
        disableSortBy: true,
        Cell: (props) => {
          const isLow = props.row.original.isLowStock;
          const thresholdPct = props.row.original.lowStockThresholdPct;
          return (
            <div style={{ textAlign: "center" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: isLow ? "#dc2626" : "#16a34a",
                  backgroundColor: isLow ? "#fee2e2" : "#dcfce7",
                }}
              >
                {isLow ? "Low Stock" : "In Stock"}
              </span>
              {thresholdPct !== undefined && thresholdPct !== null && (
                <div style={{ fontSize: "10.5px", color: "#94a3b8", marginTop: "2px" }}>
                  Threshold: {thresholdPct}%
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: t("resourcetableheaders.agencyname"),
        accessor: "agency_name",
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("resourcetableheaders.districtname"),
        accessor: "dist_name",
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("resourcetableheaders.regionname"),
        accessor: "region_name",
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      // Uncomment and modify this column if you want to show the creation date
      {
        Header: t("resourcetableheaders.facility"),
        accessor: "facility",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("resourcetableheaders.action"),
        disableSortBy: true,
        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const isMandatory = props.row.original.is_mandatory === true;
          const isDeleteDisabled = props.row.original.is_deployed || isMandatory;
          const deleteDisabledTitle = props.row.original.is_deployed
            ? "Deletion is disabled while the resource is actively deployed. Please release the resource to enable deletion"
            : isMandatory
            ? "Deletion is disabled because this resource is marked as mandatory"
            : "";
          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };
          const deleteTableDataById = async (id) => {
            let data = { id: id };
            var res = await putDataWithBody(deleteResourcebyId, data, {});
            if (res.status == "success") {
              getResourceList();
            }
            toast.success(res.msg, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000, // Close after 2 seconds
            });
            setIsdelete(false);
          };
 
          return (
            <div className="usertable__actionsicon">
              <span
                onClick={() =>
                  navigate("/resource-list/view-resource", {
                    state: { data: props.row.original },
                  })
                }
              >
                <Viewicon value={props.value} className="cursor-pointer me-2" />
              </span>

              <span
  className={`resource__table--icons ${props.row.original.is_deployed ? 'disabled-icon' : ''}`}
  onClick={() => {
    // Only allow navigation if not deployed
    if (!props.row.original.is_deployed) {
      navigate("/resource-list/create-resource", {
        state: {
          data: props.row.original.id,
          edit: true
        },
      });
    }
  }}
  style={{
    cursor: props.row.original.is_deployed ? 'not-allowed' : 'pointer',
    opacity: props.row.original.is_deployed ? 0.5 : 1
  }}
  title={props.row.original.is_deployed ? "Editing is disabled while the resource is actively deployed. Please release the resource to enable editing" : ""}
>
  <Editicon 
    className={`${props.row.original.is_deployed ? '' : 'cursor-pointer'} mr-1`}
    value={props.value} 
  />
</span>
 
              {roleName === process.env.REACT_APP_SUPER_ADMIN && (
                <span
                className={`resource__table--icons ${isDeleteDisabled ? 'disabled-icon' : ''}`}
                onClick={() => {
                  // Only allow delete if not deployed and not mandatory
                  if (!isDeleteDisabled) {
                    deletePopUp();
                  }
                }}
                style={{
                  cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDeleteDisabled ? 0.5 : 1
                }}
                title={deleteDisabledTitle}
              >
                <Deleteicon
                  value={props.value}
                  className={`${isDeleteDisabled ? '' : 'cursor-pointer'} mr-1`}
                />
              </span>
              )}
 
              {isdelete && (
                <Confirmpopup
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={""}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t, roleName]
  );

  const sourceData = resourceSrchdData != null ? resourceSrchdData : resourceData;

  const optionsFor = (configuredList, field) => buildOptions(configuredList, (resourceData || []).map((r) => r[field]));
  const nameOptions = React.useMemo(() => optionsFor(configured.names, "resource_name"), [configured.names, resourceData]);
  const categoryOptions = React.useMemo(() => optionsFor(configured.categories, "rsrc_cat_id"), [configured.categories, resourceData]);
  const agencyOptions = React.useMemo(() => optionsFor(configured.agencies, "agency_name"), [configured.agencies, resourceData]);
  const districtOptions = React.useMemo(() => optionsFor(districtData, "dist_name"), [districtData, resourceData]);
  const blockOptions = React.useMemo(() => optionsFor(configured.blocks, "region_name"), [configured.blocks, resourceData]);
  const facilityOptions = React.useMemo(() => optionsFor(configured.facilities, "facility"), [configured.facilities, resourceData]);

  const filteredResourceData = React.useMemo(
    () =>
      (sourceData || []).filter((row) => {
        if (nameFilter && norm(row.resource_name) !== norm(nameFilter)) return false;
        if (categoryFilter && norm(row.rsrc_cat_id) !== norm(categoryFilter)) return false;
        if (agencyFilter && norm(row.agency_name) !== norm(agencyFilter)) return false;
        if (districtFilter && norm(row.dist_name) !== norm(districtFilter)) return false;
        if (blockFilter && norm(row.region_name) !== norm(blockFilter)) return false;
        if (facilityFilter && norm(row.facility) !== norm(facilityFilter)) return false;
        if (stockFilter && (row.isLowStock ? "Low Stock" : "In Stock") !== stockFilter) return false;
        return true;
      }),
    [sourceData, nameFilter, categoryFilter, agencyFilter, districtFilter, blockFilter, facilityFilter, stockFilter]
  );

  const hasActiveFilters = !!(nameFilter || categoryFilter || agencyFilter || districtFilter || blockFilter || facilityFilter || stockFilter);
  const clearAllFilters = () => {
    setNameFilter("");
    setCategoryFilter("");
    setAgencyFilter("");
    setDistrictFilter("");
    setBlockFilter("");
    setFacilityFilter("");
    setStockFilter("");
  };

  const exportData = React.useMemo(() => {
    const source = filteredResourceData;
    return (source || []).map((row) => ({
      ...row,
      available_quantity: Math.max(
        (row.total_quantity || 0) - (row.damaged_quantity || 0) - (row.deploy_quantity || 0),
        0
      ),
      stockStatus: formatStockStatus(row),
    }));
  }, [filteredResourceData]);

  return (
    <div>
      <div className="create-user resource-mgmt-table">
        <style>{`
          #light .resource-mgmt-table .iccc--table th {
            background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
            color: #ffffff !important;
          }
          #light .resource-mgmt-table .iccc--table tbody tr td,
          #light .resource-mgmt-table .iccc--table tbody tr th {
            border: none !important;
          }
          #light .resource-mgmt-table .iccc--table tbody tr td {
            background-color: #fff !important;
          }
          #light .resource-mgmt-table .iccc--table tbody tr:nth-child(even) td {
            background-color: #F9FAFB !important;
          }
          .resource-mgmt-table .search-form {
            position: relative;
            display: inline-flex;
            align-items: center;
          }
          .resource-mgmt-table .search-form input[type="text"] {
            width: 320px !important;
            height: 36px !important;
            line-height: 34px !important;
            padding: 0 40px 0 12px !important;
            border: 1px solid #E4E7EC !important;
            border-radius: 8px !important;
            font-size: 12px !important;
            box-sizing: border-box !important;
          }
          .resource-mgmt-table .search-form button {
            position: absolute !important;
            top: 0 !important;
            bottom: 0 !important;
            right: 0 !important;
            height: 36px !important;
            width: 36px !important;
            margin: 0 !important;
            padding: 0 !important;
            background-color: transparent !important;
            border: none !important;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .resource-mgmt-table .search-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .resource-mgmt-table .search-icon svg {
            display: block;
            color: #2E6DA4;
          }
          .resource-mgmt-table .rsrc-district-filter {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .resource-mgmt-table .rsrc-district-filter-label {
            font-size: 10.5px;
            font-weight: 700;
            color: #2E6DA4;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            white-space: nowrap;
            margin: 0;
          }
          .resource-mgmt-table .rsrc-district-filter .category__select {
            height: 36px;
            line-height: 34px;
            padding: 0 12px;
            min-width: 170px;
            border: 1px solid #E4E7EC !important;
            border-radius: 8px !important;
            font-size: 12px !important;
            color: #0F2D42;
            background-color: #fff !important;
            box-sizing: border-box;
          }
        `}</style>
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Data"
        />
        <div style={{ background: "#fff", border: "1px solid #EEF1F5", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", padding: "10px 14px", margin: "12px 12px 0 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#EBF4FA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FilterIcon size={11} color="#2E6DA4" />
            </span>
            <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
          </div>
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>Resource Name</label>
              <select value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} style={filterControlStyle}>
                <option value="">Resource Name</option>
                {nameOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>Resource Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={filterControlStyle}>
                <option value="">Resource Category</option>
                {categoryOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>Agency Name</label>
              <select value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)} style={filterControlStyle}>
                <option value="">Agency Name</option>
                {agencyOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>District</label>
              <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} style={filterControlStyle}>
                <option value="">District</option>
                {districtOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>Block</label>
              <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)} style={filterControlStyle}>
                <option value="">Block</option>
                {blockOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>Facility</label>
              <select value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)} style={filterControlStyle}>
                <option value="">Facility</option>
                {facilityOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 190px" }}>
              <label style={filterLabelStyle}>Stock Status</label>
              <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} style={filterControlStyle}>
                <option value="">Stock Status</option>
                {STOCK_STATUS_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                style={{ height: "34px", padding: "0 12px", borderRadius: "8px", border: "1px solid #E4E7EC", background: "#F9FAFB", color: "#475467", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
        <div className="datatable__container" style={{marginTop: "10px"}}>

          {resourceData && (
            <Table
              data={filteredResourceData}
              addButtonLabel={t("resourceaddButtonLabel")}
              addNewHandler={addNewHandler}
              columns={columns}
              numberOfRows={10}
              showExport={false}
              showExcelExport={true}
              exportData={exportData}
              exportColumns={resourcemgmtcolumnsForExport}
              exportFileName="ResourceManagement"
              setSearchData={setSearchData}
              showFilter={false}
              showReset={false}
              onExpand={false}
              searchPlaceholder="Search by Resource Name, Category, Agency, District, Block or Facility"
              resourceTable={true}
              rowCount={filteredResourceData.length}
              headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF", verticalAlign: "top" }}
              tableStyle={{
                border: "1px solid #EEF1F5",
                borderRadius: "10px",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});
 
export default ResourceTable;